import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, RefreshCw, Loader2, AlertTriangle } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EVENT_TYPE_LABELS = {
  reserved_permission_grant_attempt: "Reserved permission grant attempt",
};

function formatTs(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

export default function SecurityEvents() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/security-events?limit=50");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="settings-security-page">
      <PageHeader
        testId="settings-security-header"
        title="Security events"
        subtitle="Audit trail of privilege-escalation attempts and other security-sensitive operations. Super admin only."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-security-back">
              Back to Settings
            </Button>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="settings-security-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1">Refresh</span>
            </Button>
          </div>
        }
      />

      <Card className="rounded-md">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" /> Recent events (max 50)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground" data-testid="settings-security-loading">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground italic" data-testid="settings-security-empty">
              No security events recorded.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="settings-security-table">
              <thead>
                <tr className="border-b border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="text-left py-2 px-3">Timestamp</th>
                  <th className="text-left py-2 px-3">Event</th>
                  <th className="text-left py-2 px-3">Actor</th>
                  <th className="text-left py-2 px-3">Details</th>
                  <th className="text-left py-2 px-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ev) => (
                  <tr key={ev.id} className="border-b border-border/50 last:border-0" data-testid={`security-event-${ev.id}`}>
                    <td className="py-2 px-3 whitespace-nowrap font-mono">{formatTs(ev.at)}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="h-3 w-3 text-warning" />
                        <span>{EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{ev.by_username || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">{ev.by_user_id || ""}</div>
                    </td>
                    <td className="py-2 px-3">
                      {ev.event_type === "reserved_permission_grant_attempt" ? (
                        <div className="space-y-0.5">
                          <div>
                            <span className="text-muted-foreground">Role:</span>{" "}
                            <span className="font-medium">{ev.details?.role_name || "—"}</span>
                            <span className="text-muted-foreground"> · {ev.details?.action || "—"}</span>
                          </div>
                          <div className="text-[10px] font-mono text-destructive">
                            {(ev.details?.attempted_permissions || []).join(", ")}
                          </div>
                        </div>
                      ) : (
                        <pre className="text-[10px] font-mono whitespace-pre-wrap">
                          {JSON.stringify(ev.details || {}, null, 2)}
                        </pre>
                      )}
                    </td>
                    <td className="py-2 px-3 font-mono text-muted-foreground">{ev.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
